/**
 * PRX-67 — Task department constants (D1, D7, R1, R2).
 *
 * Every task carries a department (billing | clinical | logistics |
 * blister_pack | null). Two things here are hand-mirrored from the backend and
 * MUST be changed together, or the Task Queue and the server disagree about
 * what is offered vs. what is accepted:
 *
 *  - `TASK_TYPE_DEPARTMENT` mirrors `TASK_TYPE_DEPARTMENT` in
 *    `backend-v2/src/manual-tasks/enums/task-department.enum.ts` (R2 — the map
 *    that sets a new task's department from its type).
 *  - `TASK_ASSIGNEE_ROLES_BY_DEPARTMENT` mirrors `TASK_ASSIGNEE_ROLES_BY_DEPARTMENT`
 *    added to `backend-v2/src/user-management/enums/custom-role.enum.ts` (D7 —
 *    who a task in each department may be assigned to; the server re-checks
 *    this on every assign, so the picker only decides what is OFFERED).
 *
 * `taskDepartments.drift.spec.ts` reads both backend files as text and asserts
 * the value sets match in both directions, in the shape of
 * `frontend/src/visibility/visibilityCatalogue.drift.spec.ts` (PRX-54 D6).
 *
 * NOTE: D7 in the design spec
 * (docs/superpowers/specs/2026-09-11-task-queue-departments-design.md) lists
 * the billing department's roles as "pharmacy_admin, billing" — there is no
 * `billing` CustomRole. Task 1's landed backend map
 * (`custom-role.enum.ts` `TASK_ASSIGNEE_ROLES_BY_DEPARTMENT`) resolved this
 * with `CustomRole.DATA_ENTRY_SPECIALIST` in billing's place ("Patient and
 * billing data entry" per that role's own comment) — mirrored here to match.
 */

export const TASK_DEPARTMENTS = {
  BILLING: 'billing',
  CLINICAL: 'clinical',
  LOGISTICS: 'logistics',
  BLISTER_PACK: 'blister_pack',
  // PRX-132 — PSP and Inventory queues (Rafik's "Also show on a queue").
  PSP: 'psp',
  INVENTORY: 'inventory',
} as const;

export type TaskDepartment = (typeof TASK_DEPARTMENTS)[keyof typeof TASK_DEPARTMENTS];

/** R5 order: Billing, Clinical, Logistics, Blister pack; then PSP, Inventory (PRX-132). */
export const TASK_DEPARTMENT_VALUES: readonly TaskDepartment[] = [
  TASK_DEPARTMENTS.BILLING,
  TASK_DEPARTMENTS.CLINICAL,
  TASK_DEPARTMENTS.LOGISTICS,
  TASK_DEPARTMENTS.BLISTER_PACK,
  TASK_DEPARTMENTS.PSP,
  TASK_DEPARTMENTS.INVENTORY,
];

/** R5 tab/picker copy — sentence case, exact strings. */
export const TASK_DEPARTMENT_LABELS: Record<TaskDepartment, string> = {
  billing: 'Billing',
  clinical: 'Clinical',
  logistics: 'Logistics',
  blister_pack: 'Blister pack',
  psp: 'PSP',
  inventory: 'Inventory',
};

/**
 * R2 — TaskType -> TaskDepartment map v1. Keys are the `TaskTypeKey` values
 * from `services/taskQueueService.ts` (`TASK_TYPES`), which mirror the
 * backend's `TaskType` enum. A task type not in this map resolves to `null` —
 * shown under All only (R1). None exists today: PRX-66's `blister_pack_change`
 * maps to the blister pack team.
 */
export const TASK_TYPE_DEPARTMENT: Record<string, TaskDepartment> = {
  billing_troubleshooting: TASK_DEPARTMENTS.BILLING,
  patient_counselling: TASK_DEPARTMENTS.CLINICAL,
  reach_out_to_psp: TASK_DEPARTMENTS.CLINICAL,
  reach_out_to_patient: TASK_DEPARTMENTS.CLINICAL,
  reach_out_to_md: TASK_DEPARTMENTS.CLINICAL,
  reach_out_to_insurance: TASK_DEPARTMENTS.CLINICAL,
  transfer_out: TASK_DEPARTMENTS.LOGISTICS,
  reach_out_to_ext_pharmacy: TASK_DEPARTMENTS.LOGISTICS,
  blister_pack_change: TASK_DEPARTMENTS.BLISTER_PACK,
};

/** The department a new task of this type defaults to, or null if unmapped. */
export function departmentForTaskType(taskType: string | null | undefined): TaskDepartment | null {
  if (!taskType) return null;
  return TASK_TYPE_DEPARTMENT[taskType] ?? null;
}

/**
 * D7 — roles a task in each department may be ASSIGNED to. Hand-mirrors
 * `TASK_ASSIGNEE_ROLES_BY_DEPARTMENT` in the backend's `custom-role.enum.ts`
 * (see the NOTE above the file header re: the `billing` role resolving to
 * `data_entry_specialist`).
 *
 * Deliberately NOT the acting user's permission set — this restricts who work
 * can be ROUTED TO, not who may route it (same rule as the old
 * `TASK_ASSIGNEE_ROLES` it replaces).
 */
export const TASK_ASSIGNEE_ROLES_BY_DEPARTMENT: Record<TaskDepartment, readonly string[]> = {
  billing: ['pharmacy_admin', 'data_entry_specialist'],
  clinical: ['pharmacy_admin', 'clinical_pharmacist', 'nurse'],
  logistics: ['pharmacy_admin', 'logistics', 'pharmacy_technician'],
  blister_pack: ['pharmacy_admin', 'pharmacy_technician'],
  psp: ['pharmacy_admin', 'clinical_pharmacist', 'patient_care_coordinator'],
  inventory: ['pharmacy_admin', 'inventory_manager', 'pharmacy_technician'],
};

/** Union of every department's roles — the offer for a task with no department. */
const ALL_ASSIGNEE_ROLES: readonly string[] = Array.from(
  new Set(Object.values(TASK_ASSIGNEE_ROLES_BY_DEPARTMENT).flat()),
);

/**
 * Roles a task in the given department may be assigned to. A null/undefined
 * department (legacy row, or a type with no department mapping) offers the
 * union of every department's roles rather than an empty picker.
 */
export function assignableRolesFor(department: TaskDepartment | null | undefined): readonly string[] {
  if (!department) return ALL_ASSIGNEE_ROLES;
  return TASK_ASSIGNEE_ROLES_BY_DEPARTMENT[department] ?? ALL_ASSIGNEE_ROLES;
}
